/**
 * src/api/security.js
 *
 * Handles all authentication API calls.
 * The short-lived access token is kept in the JS-readable `gaussiq_auth` cookie
 * and attached as an `Authorization: Bearer` header by the shared http.js wrapper
 * (not sent as a cookie for auth). It is deliberately NOT httpOnly — JS has to
 * read it — so XSS containment rests on it being short-lived (~15m) and on the
 * refresh token living in a separate httpOnly `gaussiq_refresh` cookie, not on
 * this cookie being unreadable.
 *
 * Functions:
 *   login(username, password)  — POST /api/security/login, stores token cookie,
 *                                 OR returns { mfaRequired: true, ... } if the
 *                                 tenant has MFA on (see the MFA section below)
 *   logout()                   — clears the auth cookie
 *   getToken()                 — retrieves the token from the cookie
 *   isAuthenticated()          — returns true if a valid (non-expired) token exists
 *   getTokenPayload()          — decodes and returns the JWT payload (no verification)
 *
 * MFA: login() returning { mfaRequired: true, stage, method, pendingToken }
 * means the credentials checked out but a second factor is required before a
 * real session token is issued — stage is 'enroll' (first time) or 'verify'
 * (already set up). Mfa.jsx drives the rest via startMfaEnroll/verifyMfaEnroll/
 * verifyMfa/resendMfaEmail below, all authenticated with `pendingToken` (a
 * short-lived token, NOT the long-lived session cookie) rather than getToken().
 * All fetches here use credentials:'include' so the gaussiq_trust cookie (set
 * by the backend on a successful MFA verify with "trust this device" checked)
 * round-trips on the next login — fetch's default is same-origin only, and the
 * API and app run on different ports in local dev.
 */

import { authedJson } from './http'

const BASE_URL = import.meta.env.VITE_API_URL
const COOKIE_NAME = 'gaussiq_auth'
const USER_COOKIE_NAME = 'gaussiq_user'
const COOKIE_EXPIRY_DAYS = 1

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

function setCookie(name, value, days) {
  const expires = new Date()
  expires.setDate(expires.getDate() + days)
  // SameSite=Strict prevents the cookie from being sent on cross-site requests.
  // Secure ensures it is only sent over HTTPS.
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `expires=${expires.toUTCString()}`,
    'path=/',
    'SameSite=Strict',
    window.location.protocol === 'https:' ? 'Secure' : '',
  ]
    .filter(Boolean)
    .join('; ')
}

function getCookie(name) {
  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.split('=')[1]) : null
}

function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`
}

/********** storeAccessToken **********************
 * Persist the short-lived access token in the auth cookie — the single place the
 * gaussiq_auth cookie is written. Both login and the refresh rotation (http.js)
 * call this, so a rotated token lands exactly where getToken() reads it.
 *
 * @param {string} token — a freshly issued JWT access token
 */
export function storeAccessToken(token) {
  setCookie(COOKIE_NAME, token, COOKIE_EXPIRY_DAYS)
}

/********** clearSession **********************
 * Drop the local session (auth token + display profile). Server-side revocation
 * is logout()'s job; this is the client-side half, also used when a refresh
 * fails (http.js) and the session is already dead server-side.
 */
export function clearSession() {
  deleteCookie(COOKIE_NAME)
  deleteCookie(USER_COOKIE_NAME)
}

// ---------------------------------------------------------------------------
// JWT helpers
// ---------------------------------------------------------------------------

/********** decodeJwt **********************
 * Decode the JWT payload without verifying the signature.
 * Verification happens server-side on every API call.
 */
function decodeJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64))
  } catch {
    return null
  }
}

/********** isTokenExpired **********************
 * Returns true if the token exists and has not passed its exp timestamp.
 */
function isTokenExpired(token) {
  const payload = decodeJwt(token)
  if (!payload?.exp) return true
  return Date.now() / 1000 > payload.exp
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/********** _completeLogin **********************
 * Shared tail end of a successful login — whether that's straight out of
 * login() (no MFA in the way) or via verifyMfa()/verifyMfaEnroll() clearing
 * an MFA challenge. Stores the JWT + display profile in cookies and returns
 * the decoded payload.
 */
function _completeLogin(data) {
  storeAccessToken(data.token)
  // The response carries display-only profile fields alongside the token.
  // Persist them separately so the navbar can render the avatar/name/email
  // without re-deriving them from the JWT. The token cookie remains the
  // source of auth.
  setCookie(
    USER_COOKIE_NAME,
    JSON.stringify({ email: data.email, name: data.name, initial: data.initial }),
    COOKIE_EXPIRY_DAYS,
  )
  return decodeJwt(data.token)
}

/********** login **********************
 * Log in with username and password.
 *
 * Three outcomes:
 *   - success: stores the JWT in a secure cookie, returns the decoded payload.
 *   - MFA required: returns { mfaRequired: true, stage, method, pendingToken }
 *     instead — no cookie is set yet. `stage` is 'enroll' (never set up MFA)
 *     or 'verify' (already enrolled); `method` is 'totp'|'email' once known
 *     (undefined for 'enroll', since the user hasn't picked one yet).
 *   - failure: throws an error with the server's detail message.
 *
 * @param {string} username
 * @param {string} password
 * @returns {Promise<object>} decoded JWT payload, or an MFA-required marker
 */
export async function login(username, password) {
  const res = await fetch(`${BASE_URL}/security/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })

  const data = await res.json()
  const mfaStatuses = ['mfa_required', 'mfa_enrollment_required']

  if (!res.ok || (data.status !== 'success' && !mfaStatuses.includes(data.status))) {
    // A 403 with a resetkey means the backend is forcing a password change
    // (temp/expired password — see routes/security.py's pwresetstate check).
    // Carry status/resetkey on the error so login.jsx can redirect to the
    // reset form instead of just showing the message as a dead-end error.
    const err = new Error(data.detail || 'Login failed. Please try again.')
    err.status = res.status
    err.resetkey = data.resetkey
    throw err
  }

  if (mfaStatuses.includes(data.status)) {
    return {
      mfaRequired: true,
      stage: data.status === 'mfa_required' ? 'verify' : 'enroll',
      method: data.method,
      pendingToken: data.pending_token,
    }
  }

  return _completeLogin(data)
}

/********** MFA — pending-token-authenticated calls **********************
 * Everything below runs during the login-time MFA challenge, before a real
 * session exists — authenticated with the short-lived `pendingToken` login()
 * returned, never with getToken(). Mirrors authedJson()'s error shape
 * (err.status/err.detail) but takes the token as a parameter instead of
 * reading the session cookie.
 */
async function pendingRequest(method, path, pendingToken, body) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    credentials: 'include', // lets the browser store a trust-device Set-Cookie
    headers: {
      ...(body !== undefined && { 'Content-Type': 'application/json' }),
      Authorization: `Bearer ${pendingToken}`,
    },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(data.detail || `API error ${res.status}: ${res.statusText}`)
    err.status = res.status
    err.detail = data.detail
    throw err
  }
  return data
}

/**
 * Begin MFA enrollment with the chosen method.
 * POST /security/mfa/enroll/start
 * @param {string} pendingToken
 * @param {'totp'|'email'} method
 * @returns {Promise<object>} 'totp': { secret_base32, otpauth_uri }; 'email': { status: 'code_sent' }
 */
export async function startMfaEnroll(pendingToken, method) {
  return pendingRequest('POST', '/security/mfa/enroll/start', pendingToken, { method })
}

/**
 * Confirm the first code for a method started with startMfaEnroll, completing
 * login. POST /security/mfa/enroll/verify
 * @param {string} pendingToken
 * @param {string} code
 * @param {boolean} [trustDevice]
 * @returns {Promise<object>} decoded JWT payload, plus `backupCodes` (TOTP
 *   only — shown exactly once, never retrievable again after this call)
 */
export async function verifyMfaEnroll(pendingToken, code, trustDevice = false) {
  const data = await pendingRequest('POST', '/security/mfa/enroll/verify', pendingToken, {
    code, trust_device: trustDevice,
  })
  return { ..._completeLogin(data), backupCodes: data.backup_codes }
}

/**
 * Verify an already-enrolled method, completing login.
 * POST /security/mfa/verify
 * @param {string} pendingToken
 * @param {string} code — a TOTP code, an email code, or (TOTP method only) a backup code
 * @param {boolean} [trustDevice]
 * @returns {Promise<object>} decoded JWT payload
 */
export async function verifyMfa(pendingToken, code, trustDevice = false) {
  const data = await pendingRequest('POST', '/security/mfa/verify', pendingToken, {
    code, trust_device: trustDevice,
  })
  return _completeLogin(data)
}

/**
 * Resend the email OTP for a pending enroll or verify challenge.
 * POST /security/mfa/resend-email
 * @param {string} pendingToken
 */
export async function resendMfaEmail(pendingToken) {
  return pendingRequest('POST', '/security/mfa/resend-email', pendingToken)
}

/**
 * Log out the current user. Revokes the session family server-side (keyed off
 * the httpOnly refresh cookie) so the token can't be refreshed again, then clears
 * the local cookies. The local clear runs even if the server call fails, so a
 * network blip can't strand the user in a half-logged-out state.
 * @returns {Promise<void>}
 */
export async function logout() {
  try {
    await fetch(`${BASE_URL}/security/logout`, { method: 'POST', credentials: 'include' })
  } catch { /* revoke best-effort — still drop the local session below */ }
  clearSession()
}

/********** getUser **********************
 * Returns the display-only user profile stored at login, or null.
 *
 * @returns {{ email: string, name: string, initial: string }|null}
 */
export function getUser() {
  const raw = getCookie(USER_COOKIE_NAME)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/********** getToken **********************
 * Retrieve the raw JWT token from the cookie.
 * Returns null if not present.
 *
 * @returns {string|null}
 */
export function getToken() {
  return getCookie(COOKIE_NAME)
}

/********** isAuthenticated **********************
 * Returns true if the user has a valid, non-expired token.
 * Call this on every protected page load to gate access.
 *
 * @returns {boolean}
 */
export function isAuthenticated() {
  const token = getToken()
  if (!token) return false
  return !isTokenExpired(token)
}

/**
 * Returns the decoded JWT payload for the current user.
 * Useful for reading tenantuid, useruid, and rights without another API call.
 * Returns null if not logged in.
 *
 * @returns {object|null} { tenantuid, useruid, rights, exp }
 */
export function getTokenPayload() {
  const token = getToken()
  if (!token) return null
  return decodeJwt(token)
}

/********** sendResetCode **********************
 * Request a password-reset code be sent to the given email address.
 * POST /security/resetcode
 *
 * @param {string} email
 * @returns {Promise<void>}
 */
export async function sendResetCode(email) {
  const res = await fetch(`${BASE_URL}/security/resetcode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || 'Failed to send reset code. Please try again.')
  }
}

/********** verifyResetCode **********************
 * Verify the reset code entered by the user.
 * POST /security/verifycode  payload: { resetKey }
 * Resolves on { message: "success" }, throws with server message otherwise.
 *
 * @param {string} resetKey
 * @returns {Promise<void>}
 */
export async function verifyResetCode(resetKey) {
  const res = await fetch(`${BASE_URL}/security/verifycode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resetKey }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.message !== 'success') {
    throw new Error(data.message || data.detail || 'Invalid or expired reset code. Please try again.')
  }
}

/********** resetPw **********************
 * Set a new password using the verified reset key.
 * POST /resetpw  payload: { resetkey, newPassword }
 *
 * @param {string} resetKey
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export async function resetPw(resetKey, newPassword) {
  const res = await fetch(`${BASE_URL}/security/resetpw`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resetkey: resetKey, newPassword }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data.message || data.detail || 'Failed to reset password. Please try again.')
  }
}

// ---------------------------------------------------------------------------
// User management (Admin > User Management tab)
// ---------------------------------------------------------------------------

// User-management and MFA-settings calls all go through the shared authedJson
// (http.js) — same Bearer + error shape as before, now with refresh-on-401 for
// free. authedJson's (method, path, body) signature matches the old helper's, so
// the call sites below are unchanged.

function initialsFromName(name) {
  const initials = (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join('')
  return initials || '?'
}

function formatLastLogin(isoString) {
  if (!isoString) return '—'
  const d = new Date(isoString)
  // last_login_at arrives as a timezone-aware ISO string (Postgres timestamptz);
  // both the date parts (getDate/getFullYear) and toLocaleTimeString below
  // resolve against the browser's local timezone with no explicit `timeZone`
  // option needed — that's the default. hour12 is what actually needed fixing:
  // this used to hand-format 24-hour HH:MM.
  const day = d.getDate()
  const month = d.toLocaleString('en-US', { month: 'short' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${day} ${month} ${d.getFullYear()}, ${time}`
}

function fromApiUser(row) {
  return {
    id: row.useruid,
    name: row.name,
    initials: initialsFromName(row.name),
    email: row.email,
    lastLogin: formatLastLogin(row.last_login_at),
    status: row.login_right === 0 ? 'disabled' : row.pwresetstate === 1 ? 'pending' : 'active',
    rights: {
      admin: Boolean(row.admin_right),
      orgSkillManagement: Boolean(row.org_skill_management),
      teamSkillManagement: Boolean(row.team_skill_management),
    },
    // 'totp' | 'email' | null (never started enrollment). mfaEnrolled is
    // false for a not-yet-confirmed TOTP secret too (see gaussiq-identity's
    // get_mfa_enrollment_status) — Reset 2FA has nothing to reset until then.
    mfaMethod: row.mfa_method,
    mfaEnrolled: Boolean(row.mfa_enrolled),
  }
}

function toApiRights(rights) {
  return {
    admin_right: rights.admin ? 1 : 0,
    org_skill_management: rights.orgSkillManagement ? 1 : 0,
    team_skill_management: rights.teamSkillManagement ? 1 : 0,
  }
}

/**
 * Fetch every user in the caller's tenant, shaped for the Admin Users tab.
 * GET /security/users — admin rights required (403 otherwise).
 * @returns {Promise<Array<{id, name, initials, email, lastLogin, status, rights}>>}
 */
export async function fetchUsers() {
  const data = await authedJson('GET', '/security/users')
  return (data.users ?? []).map(fromApiUser)
}

/**
 * Invite a new user by email. They get a generated password via email and a
 * forced reset on first login (server-side), which is why a freshly invited
 * user shows as 'pending' until they complete it.
 * POST /security/new/user
 * @param {{fullName: string, email: string, rights: {admin, orgSkillManagement, teamSkillManagement}}} params
 */
export async function inviteUser({ fullName, email, rights }) {
  await authedJson('POST', '/security/new/user', {
    name: fullName,
    email,
    rights: toApiRights(rights),
  })
}

/**
 * Enable/disable a user's ability to log in. "Remove" in the UI calls this
 * with enabled=false too — access is revoked, not the row deleted, so it's
 * reversible from the same tab.
 * PATCH /security/users/{id}/status
 */
export async function setUserEnabled(userId, enabled) {
  await authedJson('PATCH', `/security/users/${userId}/status`, { login_right: enabled ? 1 : 0 })
}

/**
 * Remove a user from the workspace. Distinct from disabling: this also drops
 * them from fetchUsers()'s results (server-side removed_at filter) instead of
 * just relabeling their row Disabled — still not a hard delete underneath.
 * POST /security/users/{id}/remove
 */
export async function removeUserAccount(userId) {
  await authedJson('POST', `/security/users/${userId}/remove`)
}

/**
 * Update one security-rights column for a user.
 * PATCH /security/users/{id}/rights
 * @param {string|number} userId
 * @param {'admin'|'orgSkillManagement'|'teamSkillManagement'} rightKey
 * @param {boolean} value
 */
export async function updateUserRight(userId, rightKey, value) {
  const apiKey = { admin: 'admin_right', orgSkillManagement: 'org_skill_management', teamSkillManagement: 'team_skill_management' }[rightKey]
  await authedJson('PATCH', `/security/users/${userId}/rights`, { rights: { [apiKey]: value ? 1 : 0 } })
}

/**
 * Regenerate a still-pending user's temp password and resend the welcome
 * email — for someone whose original invite never arrived. Throws (via
 * authedJson) if the user already completed setup.
 * POST /security/users/{id}/resend-invite
 * @param {string|number} userId
 */
export async function resendInvite(userId) {
  await authedJson('POST', `/security/users/${userId}/resend-invite`)
}

/**
 * Admin-triggered "Reset 2FA" — clears the user's MFA enrollment and
 * trusted devices (not their password) so they can set up a new
 * authenticator on their next login. For a user locked out with no backup
 * codes and no working device. The user is emailed a notice either way.
 * POST /security/users/{id}/mfa/reset
 * @param {string|number} userId
 */
export async function resetUserMfa(userId) {
  await authedJson('POST', `/security/users/${userId}/mfa/reset`)
}

// ---------------------------------------------------------------------------
// MFA settings (Admin > Security tab, super admins only, and the account
// menu's trusted devices)
// ---------------------------------------------------------------------------

/**
 * Read the tenant's current MFA settings.
 * GET /security/tenant/mfa — superadmin rights required.
 * @returns {Promise<{required: boolean, trustDeviceDays: number}>}
 */
export async function fetchTenantMfaSettings() {
  const data = await authedJson('GET', '/security/tenant/mfa')
  return { required: data.required, trustDeviceDays: data.trust_device_days }
}

/**
 * Turn MFA on/off for the tenant, and/or change the "trust this device"
 * window. PATCH /security/tenant/mfa — superadmin rights required.
 * @param {{required: boolean, trustDeviceDays?: number}} settings
 * @returns {Promise<{required: boolean, trustDeviceDays: number}>} the settings as actually saved
 */
export async function updateTenantMfaSettings({ required, trustDeviceDays }) {
  const data = await authedJson('PATCH', '/security/tenant/mfa', {
    required,
    ...(trustDeviceDays !== undefined && { trust_device_days: trustDeviceDays }),
  })
  return { required: data.required, trustDeviceDays: data.trust_device_days }
}

/**
 * The caller's own MFA method/enrollment — for the account menu's badge
 * (only shown once actually enrolled) and the trusted-devices modal's header.
 * GET /security/mfa/status — any signed-in user, not admin-gated.
 * @returns {Promise<{method: 'totp'|'email'|null, enrolled: boolean}>}
 */
export async function fetchMyMfaStatus() {
  const data = await authedJson('GET', '/security/mfa/status')
  return { method: data.method, enrolled: Boolean(data.enrolled) }
}

/**
 * List the caller's own trusted devices (account menu — every signed-in user).
 * GET /security/mfa/devices
 * @returns {Promise<Array<{id, user_agent, created_at, last_used_at, expires_at}>>}
 */
export async function listTrustedDevices() {
  const data = await authedJson('GET', '/security/mfa/devices')
  return data.devices ?? []
}

/**
 * Forget a trusted device — the next login from it re-challenges MFA instead
 * of skipping it. DELETE /security/mfa/devices/{id}
 * @param {number} deviceId
 */
export async function revokeTrustedDevice(deviceId) {
  await authedJson('DELETE', `/security/mfa/devices/${deviceId}`)
}